import { Menu } from '@base-ui/react';
import FloorPlanTable from '@components/floorPlan/FloorPlanTable';
import { TableIconCommonProps } from '@components/floorPlan/InteractiveFloorPlanTable';
import { Icon } from '@components/icon/Icon';
import { TableRating } from '@shared/types/floorPlans';
import { forwardRef, useState } from 'react';
import cx from 'classnames';
import { getTableRatingDisplay, RATING_OPTIONS } from './ratingOptionsHelper';
import styles from './TableRatingPage.scss';
import typography from '~styles/typography.scss';

export interface FloorPlanTableWithRatingProps extends TableIconCommonProps {
  id: string;
  rating: TableRating;
  onRatingChange: (id: string, rating: TableRating) => void;
}

export const FloorPlanTableWithRating = forwardRef<
  HTMLDivElement,
  FloorPlanTableWithRatingProps
>(
  (
    {
      id: floorPlanTableId,
      iconName,
      iconWidthScale,
      left,
      name,
      onRatingChange,
      orientation,
      rating,
      tableIconScale,
      testId,
      top,
    },
    ref,
  ) => {
    const [upToDateRating, setUpToDateRating] = useState(rating);
    const [menuOpen, setMenuOpen] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const updateFloorPlanTableRating = (newRating: TableRating) => {
      setUpToDateRating(newRating);
      onRatingChange(floorPlanTableId, newRating);
    };

    const selectedIndex = RATING_OPTIONS.findIndex(
      (opt) => opt.value === upToDateRating,
    );
    const fillUpToIconStyle =
      hoveredIndex !== null ? hoveredIndex : selectedIndex;

    const ratingMenuItems = RATING_OPTIONS.map(
      ({ label, value, iconName: optionIconName }, index) => {
        const isFilled = index <= fillUpToIconStyle;
        return (
          <Menu.Item
            key={label}
            className={styles.menuItem}
            onClick={() => updateFloorPlanTableRating(value)}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            title={label}
            aria-label={label}
          >
            <Icon
              name={optionIconName}
              className={cx(styles.menuItemIcon, {
                [styles.menuItemIconFilled]: isFilled,
              })}
            />
          </Menu.Item>
        );
      },
    );

    const tableRatingDisplay = getTableRatingDisplay(upToDateRating);

    const renderSelectedTableRating = () => {
      if (tableRatingDisplay.type === 'square') {
        return <Icon name="square" className={styles.tableRatingIconSvg} />;
      }
      if (tableRatingDisplay.type === 'star') {
        return <Icon name="star" className={styles.tableRatingIconSvg} />;
      }
      return Array.from({ length: tableRatingDisplay.count }, (_, i) => (
        <Icon key={i} name="fire" className={styles.tableRatingIconSvg} />
      ));
    };

    return (
      <FloorPlanTable
        left={left}
        top={top}
        tableIconScale={tableIconScale}
        data-testid={testId}
        ref={ref}
        className={cx({ [styles.selectedTable]: menuOpen })}
        title={`Floor plan table ${name}, rating ${TableRating[upToDateRating]}`}
      >
        <FloorPlanTable.Icon
          iconName={iconName}
          iconWidthScale={iconWidthScale}
          orientation={orientation}
          tableIconScale={tableIconScale}
          showHoverStroke={false}
        />
        <Menu.Root
          open={menuOpen}
          onOpenChange={(open) => {
            setMenuOpen(open);
            if (!open) setHoveredIndex(null);
          }}
        >
          <Menu.Trigger
            className={styles.ratingOverlay}
            aria-label={`${name}, rating ${TableRating[upToDateRating]}`}
          >
            <div className={cx(styles.row, typography.t3_12, styles.tableName)}>
              {name.slice(0, 4)}
            </div>
            <div className={cx(styles.row, styles.ratingIconsRow)}>
              {renderSelectedTableRating()}
            </div>
          </Menu.Trigger>
          <Menu.Portal>
            <Menu.Positioner
              side="top"
              sideOffset={12}
              className={styles.positioner}
            >
              <Menu.Popup className={cx(styles.popup)}>
                <Menu.Arrow className={styles.arrow}>
                  <Icon name="arrowPopover" />
                </Menu.Arrow>
                {ratingMenuItems}
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
      </FloorPlanTable>
    );
  },
);
